<template>
  <v-container
    ref="sectionRoot"
    class="planes-section py-8 py-md-12"
    :class="{ 'is-visible': visible }"
  >
    <!-- Encabezado -->
    <v-row id="seccionPlanes" class="text-center mb-4 mb-md-8 fade-slide-down">
      <v-col cols="12" md="10" lg="8" class="mx-auto">
        <div class="section-badge mb-3">
          <span class="section-badge__dot"></span>
          Planes
        </div>

        <h2 class="header-title mb-3">
          Elige el plan ideal para ti
        </h2>

        <p class="header-subtitle mb-0">
          Filtra según tus necesidades y encuentra la opción que mejor se adapta a ti y tu familia.
        </p>
      </v-col>
    </v-row>

    <!-- Filtro -->
    <v-row justify="center" class="mb-6 mb-md-8">
      <v-col cols="12" class="text-center">
        <v-btn-toggle
          v-model="filtros.tipo"
          mandatory
          rounded
          class="tipo-toggle"
        >
          <v-btn value="Todos">Todos</v-btn>
          <v-btn value="Individual">Individual</v-btn>
          <v-btn value="Familiar">Familiar</v-btn>
        </v-btn-toggle>
      </v-col>
    </v-row>

    <!-- Resultados -->
    <v-row justify="center" class="mb-2">
      <v-col cols="12" md="11" lg="10">
        <div class="results-text mb-3">
          {{ planesFiltrados.length }}
          {{ planesFiltrados.length === 1 ? 'plan encontrado' : 'planes encontrados' }}
        </div>
      </v-col>
    </v-row>

    <!-- Cards -->
    <v-row justify="center" dense>
      <v-col
        v-for="(plan, index) in planesFiltrados"
        :key="plan.id"
        class="plan-col d-flex px-2 py-2"
        :style="{ animationDelay: `${index * 0.1}s` }"
      >
        <v-card class="plan-card d-flex flex-column flex-grow-1" flat>
          <span class="plan-card__accent"></span>

          <div class="plan-card__top">
            <div class="plan-type mb-2">
              {{ plan.tipo }} · {{ plan.hospital }}
            </div>

            <h3 class="plan-title mb-2">
              {{ plan.title }}
            </h3>

            <div class="price-box mb-4">
              <div class="price-amount">
                {{ formatPrice(plan.costo) }}
              </div>
              <div class="price-label">
                Pago mensual
              </div>
            </div>
          </div>

          <v-card-text class="pt-0 flex-grow-1">
            <ul class="benefits-list">
              <li
                v-for="(item, i) in plan.beneficios"
                :key="`${plan.id}-beneficio-${i}`"
                class="benefit-item"
              >
                <v-icon color="success" small class="benefit-icon">
                  mdi-check-circle
                </v-icon>
                <span class="benefit-text" v-html="item"></span>
              </li>
            </ul>
          </v-card-text>

          <v-card-actions class="pt-0 pb-5 px-5">
            <v-btn
              block
              large
              class="whatsapp-btn font-weight-bold"
              @click="whatsappLink(plan.title)"
            >
              <v-icon left>mdi-whatsapp</v-icon>
              Cotizar por WhatsApp
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>

    </v-row>
  </v-container>
</template>

<script>
export default {
  name: 'SeccionPlanesComponent',

  data() {
    return {
      visible: false,

      filtros: {
        tipo: 'Todos'
      },

      // "50" en el nombre es la nomenclatura oficial de esos dos planes,
      // no un filtro de edad aparte - por eso el filtro de edad se quitó.

      // Precios verificados contra las cotizaciones oficiales (agosto 2026).
      // Suma asegurada corregida de $65,000 a $70,000 en todos los planes.
      planes: [
        {
          id: '1',
          title: 'Plus Familiar',
          costo: 1473,
          tipo: 'Familiar',
          edad: 'General',
          hospital: 'Red General',
          beneficios: [
            'Atención de urgencias con cobertura de <b>$70,000 MXN</b> en toda nuestra red hospitalaria',
            'Incluye a tus <b>familiares directos</b> como beneficiarios',
            'Consultas médicas con <b>especialistas</b>: 6 LUC disponibles para ti y los beneficiarios de tu familia',
            'Paquete de consultas médicas con <b>tarifa especial</b> si las agotas antes de la vigencia del plan'
          ]
        },
        {
          id: '2',
          title: 'La Bene Familiar',
          costo: 1271,
          tipo: 'Familiar',
          edad: 'General',
          hospital: 'La Bene',
          beneficios: [
            'Atención de urgencias con cobertura de <b>$70,000 MXN</b> en Hospital La Bene San Luis',
            'Incluye a tus <b>familiares directos</b> como beneficiarios',
            'Consultas médicas con <b>especialistas</b>: 6 LUC disponibles para ti y los beneficiarios de tu familia',
            'Paquetes de consultas médicas con <b>tarifa especial</b> si las agotas antes de la vigencia del plan'
          ]
        },
        {
          id: '3',
          title: 'Plus Individual',
          // AVISO: no encontré una cotización oficial para esta variante
          // específica (sin el sufijo "50"). Verifica este precio contra
          // tu sistema de cotización antes de publicar - los otros 4
          // planes ya están confirmados contra cotizaciones reales.
          costo: 650,
          tipo: 'Individual',
          edad: 'General',
          hospital: 'Red General',
          beneficios: [
            'Atención de urgencias con cobertura de <b>$70,000 MXN</b> en toda nuestra red hospitalaria',
            'Consultas médicas con <b>especialistas</b>: 6 LUC disponibles inmediatamente',
            'Paquete de consultas médicas con <b>tarifa especial</b> si las agotas antes de la vigencia del plan'
          ]
        },
        {
          id: '4',
          title: 'Plus Individual 50',
          costo: 1254,
          tipo: 'Individual',
          edad: 'Mayores de 50',
          hospital: 'Red General',
          beneficios: [
            'Diseñado especialmente para quienes tienen más de 50 años',
            'Atención de urgencias con cobertura de <b>$70,000 MXN</b> en toda nuestra red hospitalaria',
            'Consultas médicas con <b>especialistas</b>: 6 LUC disponibles inmediatamente',
            'Paquete de consultas médicas con <b>tarifa especial</b> si las agotas antes de la vigencia del plan'
          ]
        },
        {
          id: '5',
          title: 'La Bene Individual 50',
          costo: 1123,
          tipo: 'Individual',
          edad: 'Mayores de 50',
          hospital: 'La Bene',
          beneficios: [
            'Diseñado especialmente para quienes tienen más de 50 años',
            'Atención de urgencias con cobertura de <b>$70,000 MXN</b> en Hospital La Bene San Luis',
            'Consultas médicas con <b>especialistas</b>: 6 LUC disponibles inmediatamente',
            'Paquete de consultas médicas con <b>tarifa especial</b> si las agotas antes de la vigencia del plan'
          ]
        }
      ]
    };
  },

  computed: {
    planesFiltrados() {
      if (this.filtros.tipo === 'Todos') return this.planes;
      return this.planes.filter(plan => plan.tipo === this.filtros.tipo);
    }
  },

  mounted() {
    const el = this.$refs.sectionRoot?.$el || this.$refs.sectionRoot;
    if (!el || typeof IntersectionObserver === 'undefined') {
      this.visible = true;
      return;
    }

    this._observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.visible = true;
          this._observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    this._observer.observe(el);
  },

  beforeDestroy() {
    this._observer?.disconnect();
  },

  methods: {
    formatPrice(value) {
      // Sin decimales a propósito: todos los precios de tus planes son
      // números redondos, así que el ".00" solo ocupaba espacio de más.
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        maximumFractionDigits: 0
      }).format(value);
    },

    whatsappLink(plan) {
      const telefono = process.env.VUE_APP_BASE_PHONEWHATSWEB;
      const mensaje = `Hola, quisiera una cotización de ${plan}`;
      const url = `https://wa.me/${telefono}?text=${encodeURIComponent(mensaje)}`;
      window.open(url, '_blank');
    }
  }
};
</script>

<style scoped>
.planes-section {
  background: linear-gradient(180deg, #ffffff 0%, #f5f9fd 100%);
  border-radius: 24px;
}

.section-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 16px 7px 12px;
  border-radius: 999px;
  background: #dceeff;
  color: #1b4f8c;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.4px;
}

.section-badge__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #1b4f8c;
}

.fade-slide-down {
  opacity: 0;
  transform: translateY(-24px);
}

.is-visible .fade-slide-down {
  animation: fadeSlideDown 0.8s ease forwards;
}

@keyframes fadeSlideDown {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.header-title {
  font-size: clamp(1.8rem, 3vw, 2.6rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: #16233a;
}

.header-subtitle {
  font-size: 1rem;
  line-height: 1.7;
  color: #64748b;
  max-width: 720px;
  margin: 0 auto;
}

.tipo-toggle {
  border-radius: 999px !important;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.06);
}

.tipo-toggle .v-btn {
  text-transform: none !important;
  font-weight: 600;
  letter-spacing: 0.2px;
  color: #475569 !important;
}

.tipo-toggle .v-btn.v-item--active {
  background: #1b4f8c !important;
  color: #ffffff !important;
}

.results-text {
  font-size: 0.95rem;
  color: #64748b;
  font-weight: 600;
}

/* Ancho de cada tarjeta por breakpoint. Se usa una clase propia en vez
   de los props cols/sm/lg de Vuetify porque 5 no divide limpio en su
   grid de 12 columnas. */
.plan-col {
  flex: 0 0 100%;
  max-width: 100%;
}

@media (min-width: 600px) {
  .plan-col {
    flex: 0 0 50%;
    max-width: 50%;
  }
}

@media (min-width: 960px) {
  .plan-col {
    flex: 0 0 33.333%;
    max-width: 33.333%;
  }
}

@media (min-width: 1264px) {
  .plan-col {
    flex: 0 0 20%;
    max-width: 20%;
  }

  /* Con 5 tarjetas en una fila hay poco ancho por tarjeta, así que se
     compacta el texto de forma más agresiva que en los demás breakpoints */
  .plan-card__top {
    padding: 20px 14px 6px;
  }

  .plan-type {
    font-size: 0.72rem;
  }

  .plan-title {
    font-size: 1.05rem;
    line-height: 1.2;
  }

  .price-box {
    padding: 12px 8px;
  }

  .price-amount {
    font-size: 1.25rem;
    white-space: nowrap;
  }

  .price-label {
    font-size: 0.78rem;
  }

  .benefit-text {
    font-size: 0.82rem;
    line-height: 1.5;
  }
}

/* Card: sin borde, sombra suave, radio grande, barra de acento en hover
   -- mismo lenguaje visual que seccion-cob para que todo el sitio se
   sienta como un solo sistema de diseño */
.plan-card {
  position: relative;
  height: 100%;
  border-radius: 28px;
  background: #ffffff;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.05);
  overflow: hidden;
  opacity: 0;
  transform: translateY(24px);
  transition:
    transform 0.32s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.32s ease;
}

.is-visible .plan-card {
  animation: fadeInUp 0.55s ease forwards;
}

.plan-card__accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #1b4f8c, #4f8fd6);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.32s ease;
}

.plan-card:hover .plan-card__accent {
  transform: scaleX(1);
}

.plan-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(27, 79, 140, 0.16);
}

.plan-card__top {
  padding: 28px 24px 8px;
  text-align: center;
}

.plan-type {
  font-size: 0.82rem;
  font-weight: 700;
  color: #1b4f8c;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.plan-title {
  font-size: 1.35rem;
  font-weight: 800;
  line-height: 1.25;
  color: #16233a;
}

.price-box {
  padding: 18px 16px;
  border-radius: 18px;
  background: linear-gradient(180deg, #f4f9fd 0%, #ffffff 100%);
  border: 1px solid #dceeff;
}

.price-amount {
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  font-weight: 800;
  line-height: 1.1;
  color: #111827;
}

.price-label {
  margin-top: 6px;
  font-size: 0.9rem;
  color: #64748b;
}

.benefits-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.benefit-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 14px;
}

.benefit-icon {
  margin-top: 2px;
  flex-shrink: 0;
}

.benefit-text {
  flex: 1;
  display: block;
  color: #475569;
  font-size: 0.96rem;
  line-height: 1.6;
  word-break: break-word;
}

.whatsapp-btn {
  text-transform: none !important;
  border-radius: 999px !important;
  letter-spacing: 0.2px;
  background-color: #25d366 !important;
  color: #ffffff !important;
  box-shadow: 0 4px 14px rgba(37, 211, 102, 0.35);
  transition:
    transform 0.24s ease,
    box-shadow 0.24s ease,
    background-color 0.24s ease;
}

.whatsapp-btn:hover {
  background-color: #1ebe5a !important;
  transform: translateY(-2px);
  box-shadow: 0 8px 22px rgba(37, 211, 102, 0.45);
}

.whatsapp-btn:active {
  transform: translateY(0);
}

.whatsapp-btn .v-icon {
  font-size: 20px;
}

@media (prefers-reduced-motion: reduce) {
  .whatsapp-btn:hover {
    transform: none;
  }
}

@keyframes fadeInUp {
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .fade-slide-down,
  .plan-card {
    opacity: 1;
    transform: none;
    animation: none;
  }

  .plan-card:hover {
    transform: none;
  }
}

@media (max-width: 600px) {
  .planes-section {
    border-radius: 18px;
  }

  .plan-card__top {
    padding: 20px 20px 8px;
  }

  .price-box {
    padding: 16px 12px;
  }
}
</style>